import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { downloadBlob } from '../lib/download-blob';
import type { RunEventRow } from '../lib/fleetStream';
import { Button } from './ui/Button';

// Run transcript export. The control shipped PERMANENTLY DISABLED with a comment
// blaming "#812 isn't built yet" — but #812 is the SESSION jsonl export, a
// different capability, and a plain-JSON run transcript has been served all
// along at `GET /v1/tasks/:id/events`.
//
// It exports the rows THE VIEW HOLDS rather than re-fetching that route, for two
// reasons: what a reader asked to keep is what they are looking at (the route
// pages on `?since_seq=` and would hand back a different, cursor-shaped slice),
// and the stream has already backfilled the whole run, so a second round trip
// buys nothing. That also keeps the export honest while the stream is still
// tailing — the file is what had arrived when the button was pressed.
//
// Disabled only while there is genuinely nothing to write, and a browser that
// refuses the download SAYS so (the `copyText` rule: a write that did not land
// must not read as "the click did nothing").

/** Serialize the held rows as pretty JSON — the DTO exactly as the route emitted it. */
export function runTranscriptJson(events: readonly RunEventRow[]): string {
  return JSON.stringify(events, null, 2);
}

/** Hand the browser a file. Answers whether it started, never throws. */
export function downloadJson(filename: string, body: string): boolean {
  try {
    downloadBlob(filename, new Blob([body], { type: 'application/json' }));
    return true;
  } catch {
    return false;
  }
}

export type RunExportButtonProps = {
  readonly runId: string;
  readonly events: readonly RunEventRow[];
};

export function RunExportButton(props: RunExportButtonProps): JSX.Element {
  const [failed, setFailed] = createSignal(false);
  const run = (): void => {
    setFailed(!downloadJson(`${props.runId}.json`, runTranscriptJson(props.events)));
  };
  return (
    <>
      <Button variant="outline" disabled={props.events.length === 0} onClick={run}>
        {t('fleet.export_json')}
      </Button>
      <Show when={failed()}>
        <span class="timeline__meta" role="alert">
          {t('fleet.export_failed')}
        </span>
      </Show>
    </>
  );
}
